/**
 * DownloadButton Component
 *
 * Provides a download interface for exporting patient measurement points and area data
 * as a JSON file. The downloaded file includes both the points array and area calculation
 * with a timestamped filename for easy identification.
 */

import React from "react";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useTheme } from "@mui/material";
import { Vector3 } from "three";

import ControlPanelButton from "./control-panel-button";

interface DownloadButtonProps {
  /** Whether the download button should be disabled */
  disabled: boolean;
  /** Array of measurement points to be downloaded */
  points: Vector3[];
  /** Calculated area value to be included in the download */
  area: number;
  /** Patient identifier used in the filename */
  patientId: string;
}

const DownloadPointsButton: React.FC<DownloadButtonProps> = ({
  disabled,
  points,
  area,
  patientId,
}) => {

  // File configuration
  const extension = ".json";
  const timeString = new Date().toLocaleDateString().replace(/\//g, "-");
  const fileName = `Patient ${patientId} Points ${timeString}${extension}`;

  /**
   * Initiates the download process for patient measurement data
   * Creates a JSON blob containing points and area data, then triggers browser download
   */
  const startDownloadPoints = () => {
    // Prepare data payload for download
    const data = { points, area };
    const pointsString = JSON.stringify(data);

    // Create downloadable blob and trigger download
    const blob = new Blob([pointsString], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    // Create temporary anchor element for download
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();

    // Clean up temporary elements and object URL
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <ControlPanelButton
      disabled={disabled}
      icon={<FileDownloadIcon color={"action"} />}
      onClick={startDownloadPoints}
      activeTooltip="Download Points"
      inactiveTooltip="Not enough points to download"
      tooltipPlacement="right"
    />
  );
};

export default DownloadPointsButton;
